import React, { useState, useEffect } from 'react';
import { adminAPI, settingsAPI } from '../../utils/api';
import { Plus, Trash2, Upload, Image as ImageIcon, Edit2, X, Timer } from 'lucide-react';
import { toast } from 'sonner';

const emptyBanner = { title: '', subtitle: '', image_url: '', link_url: '', position: 'homepage_hero', active: true, sort_order: 0, show_title: true };

// Per-position image guidance shown inline in the editor so admins don't
// have to remember sizes. Keep this in sync with HomeBanners.js render sizes.
const BANNER_SPECS = {
  homepage_hero: {
    label: 'Homepage Hero',
    recommended: '1920 × 640 px',
    minimum: '1440 × 480 px',
    ratio: '3 : 1',
    maxSizeKB: 400,
    formats: 'JPG · WebP · PNG',
    notes: 'Shows under the search bar. The uploaded image renders as-is with no dark overlay; the Title/Subtitle can be shown or hidden per banner with the Show Title toggle below.',
  },
  homepage_strip: {
    label: 'Homepage Strip',
    recommended: '960 × 288 px',
    minimum: '640 × 192 px',
    ratio: '10 : 3 (≈ 3.33 : 1)',
    maxSizeKB: 150,
    formats: 'JPG · WebP',
    notes: 'Auto-scrolling carousel of cards. Centre the key visual — each card is on-screen ~3 seconds.',
  },
};

const SizeGuide = ({ position }) => {
  const spec = BANNER_SPECS[position] || BANNER_SPECS.homepage_hero;
  return (
    <div className="bg-[#f0edff] border border-[#1800ad]/20 rounded-lg p-2.5 text-[11px] text-[#0A0B10] space-y-1 mt-2" data-testid="banner-size-guide">
      <p className="font-bold text-[#1800ad] flex items-center gap-1.5">
        <ImageIcon size={11} /> {spec.label} — image guidelines
      </p>
      <ul className="space-y-0.5 pl-1">
        <li><span className="text-[#6B7280]">Recommended:</span> <span className="font-bold">{spec.recommended}</span></li>
        <li><span className="text-[#6B7280]">Minimum:</span> <span className="font-bold">{spec.minimum}</span></li>
        <li><span className="text-[#6B7280]">Aspect ratio:</span> <span className="font-bold">{spec.ratio}</span></li>
        <li><span className="text-[#6B7280]">Max file size:</span> <span className="font-bold">{spec.maxSizeKB} KB</span></li>
        <li><span className="text-[#6B7280]">Formats:</span> <span className="font-bold">{spec.formats}</span></li>
      </ul>
      <p className="text-[10px] text-[#4B5563] italic pt-1 border-t border-[#1800ad]/10">{spec.notes}</p>
    </div>
  );
};

const AdminBannersTab = () => {
  const [banners, setBanners] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {...} = edit
  const [uploading, setUploading] = useState(false);
  // Hero autoplay interval (seconds). Loaded from /api/settings and saved via
  // /api/admin/settings. Controls how quickly HomeBanners.HeroCarousel advances.
  const [autoplaySec, setAutoplaySec] = useState(3);
  const [savingAutoplay, setSavingAutoplay] = useState(false);

  const load = async () => {
    try { const res = await adminAPI.listBanners(); setBanners(res.data); }
    catch (e) { toast.error('Failed to load banners'); }
  };
  useEffect(() => {
    load();
    // Also load the current autoplay setting so the dropdown shows the truth.
    settingsAPI.get()
      .then((r) => {
        const secs = Number(r?.data?.hero_autoplay_seconds);
        if (Number.isFinite(secs) && secs >= 2 && secs <= 15) setAutoplaySec(secs);
      })
      .catch(() => { /* keep default */ });
  }, []);

  const handleAutoplayChange = async (e) => {
    const secs = Number(e.target.value);
    setAutoplaySec(secs);
    setSavingAutoplay(true);
    try {
      await adminAPI.updateSettings({ hero_autoplay_seconds: secs });
      toast.success(`Hero auto-scroll set to ${secs} second${secs !== 1 ? 's' : ''}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save setting');
    } finally {
      setSavingAutoplay(false);
    }
  };

  const save = async () => {
    if (!editing.title?.trim() || !editing.image_url?.trim()) { toast.error('Title and image are required'); return; }
    try {
      if (editing.banner_id) await adminAPI.updateBanner(editing.banner_id, editing);
      else await adminAPI.createBanner(editing);
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try { await adminAPI.deleteBanner(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };
  const uploadImg = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    // Soft warning if file exceeds recommended size for the selected position
    const spec = BANNER_SPECS[editing.position] || BANNER_SPECS.homepage_hero;
    const sizeKB = Math.round(file.size / 1024);
    if (sizeKB > spec.maxSizeKB) {
      toast.warning(`Image is ${sizeKB} KB — recommended max is ${spec.maxSizeKB} KB for ${spec.label}. Consider compressing at tinypng.com for faster page load.`);
    }
    setUploading(true);
    try {
      const res = await adminAPI.uploadBannerImage(file);
      setEditing({ ...editing, image_url: res.data.url });
      toast.success('Uploaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-3" data-testid="admin-banners-tab">
      {/* Hero auto-scroll timing selector — controls the HomeBanners hero carousel interval site-wide. */}
      <div className="rounded-xl border border-[#1800ad]/20 bg-[#f0edff] p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid="hero-autoplay-row">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Timer size={16} className="text-[#1800ad] flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs sm:text-sm font-bold text-[#0A0B10]">Hero Banner Auto-Scroll</p>
            <p className="text-[11px] text-[#6B7280]">How long each hero banner is shown before auto-advancing</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={autoplaySec}
            onChange={handleAutoplayChange}
            disabled={savingAutoplay}
            data-testid="hero-autoplay-select"
            className="rounded-lg border border-[#1800ad]/30 bg-white px-3 py-1.5 text-sm font-bold text-[#1800ad] disabled:opacity-60"
          >
            {[3, 4, 5, 6, 7].map((s) => (
              <option key={s} value={s}>{s} second{s !== 1 ? 's' : ''}</option>
            ))}
          </select>
          {savingAutoplay && <span className="text-[11px] text-[#4B5563]">Saving…</span>}
        </div>
      </div>
      <div className="flex justify-between items-center">
        <p className="text-xs sm:text-sm text-[#6B7280]">{banners.length} banner{banners.length !== 1 ? 's' : ''}</p>
        <button onClick={() => setEditing({ ...emptyBanner })} data-testid="new-banner-btn"
          className="bg-[#1800ad] text-white rounded-lg px-3 py-1.5 text-xs sm:text-sm font-bold flex items-center gap-1.5">
          <Plus size={14} /> New Banner
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {banners.map((b) => (
          <div key={b.banner_id} className="bg-white rounded-xl p-3 border border-gray-100" data-testid={`banner-${b.banner_id}`}>
            <div className="aspect-[3/1] bg-gray-100 rounded-lg overflow-hidden mb-2">
              {b.image_url ? <img src={b.image_url.startsWith('http') ? b.image_url : `${process.env.REACT_APP_BACKEND_URL}${b.image_url}`} alt={b.title} className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full text-gray-400"><ImageIcon size={32} /></div>}
            </div>
            <p className="font-bold text-sm text-[#0A0B10]">{b.title}</p>
            <p className="text-xs text-[#6B7280] truncate">{b.subtitle}</p>
            <div className="flex items-center gap-2 mt-2 text-[10px]">
              <span className="px-1.5 py-0.5 rounded bg-[#f8f9fa] font-bold text-[#4B5563]">{b.position}</span>
              <span className={`px-1.5 py-0.5 rounded font-bold ${b.active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>{b.active ? 'ACTIVE' : 'INACTIVE'}</span>
              <span className="text-[#9CA3AF]">#{b.sort_order}</span>
              <div className="ml-auto flex gap-1">
                <button onClick={() => setEditing(b)} className="p-1 rounded hover:bg-[#f0edff] text-[#1800ad]" data-testid={`edit-banner-${b.banner_id}`}><Edit2 size={12} /></button>
                <button onClick={() => del(b.banner_id)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`del-banner-${b.banner_id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {banners.length === 0 && (
          <div className="md:col-span-2 bg-white rounded-xl p-8 text-center text-sm text-[#9CA3AF]">No banners yet — create one to feature on the homepage hero.</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="banner-editor">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <h3 className="font-bold text-[#0A0B10]">{editing.banner_id ? 'Edit Banner' : 'New Banner'}</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded bg-gray-100"><X size={14} /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Title" data-testid="banner-title"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 outline-none focus:border-[#1800ad]" />
              <input value={editing.subtitle} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} placeholder="Subtitle (optional)" data-testid="banner-subtitle"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 outline-none focus:border-[#1800ad]" />
              <div>
                <p className="text-xs font-bold text-[#0A0B10] mb-1">Image</p>
                {editing.image_url && (
                  <img src={editing.image_url.startsWith('http') ? editing.image_url : `${process.env.REACT_APP_BACKEND_URL}${editing.image_url}`} alt="" className="w-full aspect-[3/1] object-cover rounded-lg mb-2" />
                )}
                <input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder="Paste image URL or upload below" data-testid="banner-image-url"
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mb-2 outline-none focus:border-[#1800ad]" />
                <label className="flex items-center justify-center gap-2 bg-[#f0edff] hover:bg-[#1800ad] hover:text-white text-[#1800ad] rounded-lg px-3 py-2 cursor-pointer transition-colors">
                  <Upload size={14} />{uploading ? 'Uploading…' : 'Upload image'}
                  <input type="file" accept="image/*" onChange={uploadImg} className="hidden" data-testid="banner-upload-input" disabled={uploading} />
                </label>
                <SizeGuide position={editing.position} />
              </div>
              <input value={editing.link_url} onChange={(e) => setEditing({ ...editing, link_url: e.target.value })} placeholder="Click-through URL (optional)" data-testid="banner-link"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 outline-none focus:border-[#1800ad]" />
              <div className="grid grid-cols-3 gap-2">
                <select value={editing.position} onChange={(e) => setEditing({ ...editing, position: e.target.value })} data-testid="banner-position"
                  className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 text-xs">
                  <option value="homepage_hero">Homepage Hero</option>
                  <option value="homepage_strip">Homepage Strip</option>
                </select>
                <input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })}
                  placeholder="Order" data-testid="banner-order"
                  className="rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 text-xs" />
                <label className="flex items-center gap-1 text-xs font-bold text-[#0A0B10] bg-gray-50 rounded-lg px-2 py-2">
                  <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="banner-active" /> Active
                </label>
              </div>
              {/* iter139 — Show/Hide Title toggle (per banner). Only affects
                   the hero title/subtitle text block; the uploaded image is
                   always rendered as-is. Undefined on older banners = shown. */}
              <label className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2 text-xs font-bold text-[#0A0B10]" data-testid="banner-show-title-row">
                <span>Show Title on banner</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={editing.show_title !== false}
                  data-testid="banner-show-title-toggle"
                  onClick={() => setEditing({ ...editing, show_title: !(editing.show_title !== false) })}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${editing.show_title !== false ? 'bg-[#1800ad]' : 'bg-gray-300'}`}
                >
                  <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform" style={{ transform: editing.show_title !== false ? 'translateX(18px)' : 'translateX(3px)' }} />
                </button>
              </label>
              <div className="flex gap-2 pt-2">
                <button onClick={save} data-testid="banner-save" className="flex-1 bg-[#1800ad] hover:bg-[#1200a0] text-white rounded-lg px-4 py-2 font-bold">Save</button>
                <button onClick={() => setEditing(null)} className="bg-gray-200 text-[#4B5563] rounded-lg px-4 py-2 font-bold">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBannersTab;
